import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import APIKit from '../../../config/APIKit';
import Title from '../settings/Title';
import style from '../settings/settings.module.scss';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../store/compatibility';

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function MetaAdsIntegration() {
  const dispatch = useDispatch();
  const query = useQueryParams();

  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [status, setStatus] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsPaging, setLeadsPaging] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/status');
      if (res?.success) setStatus(res.data);
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load Meta Ads status', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const loadPages = useCallback(async () => {
    setPagesLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/pages');
      if (res?.success) setPages(res.data || []);
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load Facebook Pages', severity: 'error' }));
    } finally {
      setPagesLoading(false);
    }
  }, [dispatch]);

  const loadLeadGenForms = useCallback(
    async (pageId) => {
      if (!pageId) {
        setForms([]);
        return;
      }
      setFormsLoading(true);
      try {
        const res = await APIKit.get('/integrations/meta-ads/leadgen-forms', {
          params: { page_id: pageId },
        });
        if (res?.success) setForms(res.data || []);
      } catch (e) {
        dispatch(showSnackBar({ message: 'Failed to load lead forms', severity: 'error' }));
        setForms([]);
      } finally {
        setFormsLoading(false);
      }
    },
    [dispatch]
  );

  const loadAdAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/ad-accounts');
      if (res?.success) setAdAccounts(res.data || []);
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load ad accounts', severity: 'error' }));
    } finally {
      setAccountsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (query.get('connected') === '1') {
      dispatch(showSnackBar({ message: 'Meta Ads connected successfully', severity: 'success' }));
      loadStatus();
    }
    const err = query.get('error');
    if (err) {
      dispatch(showSnackBar({ message: err, severity: 'error' }));
    }
  }, [dispatch, loadStatus, query]);

  useEffect(() => {
    if (status?.connected) {
      loadAdAccounts();
      loadPages();
    } else {
      setAdAccounts([]);
      setPages([]);
      setForms([]);
      setLeads([]);
      setSelectedPageId('');
      setSelectedFormId('');
    }
  }, [loadAdAccounts, loadPages, status?.connected]);

  useEffect(() => {
    loadLeadGenForms(selectedPageId);
    setSelectedFormId('');
    setLeads([]);
    setLeadsPaging(null);
  }, [loadLeadGenForms, selectedPageId]);

  const loadLeads = async (after) => {
    if (!selectedPageId || !selectedFormId) {
      dispatch(showSnackBar({ message: 'Select a Page and a Lead Form', severity: 'warning' }));
      return;
    }
    setLeadsLoading(true);
    try {
      const res = await APIKit.get('/integrations/meta-ads/leads', {
        params: {
          page_id: selectedPageId,
          form_id: selectedFormId,
          limit: 50,
          ...(after ? { after } : {}),
        },
      });
      if (res?.success) {
        setLeads((prev) => (after ? [...prev, ...(res.data || [])] : res.data || []));
        setLeadsPaging(res.paging || null);
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to load leads from Meta', severity: 'error' }));
    } finally {
      setLeadsLoading(false);
    }
  };

  const formatFieldData = (fieldData) => {
    if (!Array.isArray(fieldData)) return '—';
    return fieldData
      .map((f) => {
        const label = f.name || 'field';
        const val = Array.isArray(f.values) ? f.values.join(', ') : String(f.values ?? '');
        return `${label}: ${val}`;
      })
      .join(' · ');
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await APIKit.post('/integrations/meta-ads/connect', {});
      const url = res?.data?.authUrl;
      if (!url) throw new Error('Missing auth URL');
      window.location.href = url;
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to start Meta Ads connection', severity: 'error' }));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await APIKit.post('/integrations/meta-ads/disconnect', {});
      if (res?.success) {
        dispatch(showSnackBar({ message: 'Disconnected Meta Ads', severity: 'success' }));
        await loadStatus();
      }
    } catch (e) {
      dispatch(showSnackBar({ message: 'Failed to disconnect', severity: 'error' }));
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = Boolean(status?.connected);

  return (
    <div className={style.container}>
      <Title title="Meta Ads Integration" enableButton={false} />

      <Paper
        sx={{
          mt: 2,
          p: 3,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(102, 126, 234, 0.1)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            zIndex: 1,
          },
        }}
      >
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Connection Status
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Connect your Meta (Facebook/Instagram) Ads account to sync ad accounts and use Lead Ads/webhooks later.
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              {loading ? (
                <CircularProgress size={22} />
              ) : (
                <Chip
                  label={connected ? 'Connected' : 'Not connected'}
                  sx={{
                    fontWeight: 700,
                    color: connected ? '#ffffff' : '#666',
                    background: connected
                      ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                      : 'rgba(0,0,0,0.08)',
                  }}
                />
              )}

              {!connected ? (
                <Button
                  variant="contained"
                  disabled={connecting}
                  onClick={handleConnect}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {connecting ? 'Connecting…' : 'Connect Meta Ads'}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={disconnecting}
                  onClick={handleDisconnect}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                  }}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              )}
            </Box>
          </Box>

          <Divider />

          {connected && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                Connected Meta User
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {status?.meta?.user?.name || '—'} ({status?.meta?.user?.id || '—'})
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
              Ad Accounts
            </Typography>

            {!connected ? (
              <Typography variant="body2" color="textSecondary">
                Connect Meta Ads to view ad accounts.
              </Typography>
            ) : accountsLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">
                  Loading ad accounts…
                </Typography>
              </Box>
            ) : adAccounts.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No ad accounts found (or your token doesn’t have access).
              </Typography>
            ) : (
              <Stack spacing={1}>
                {adAccounts.slice(0, 10).map((a) => (
                  <Paper
                    key={a.id}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: '14px', borderColor: 'rgba(102, 126, 234, 0.2)' }}
                  >
                    <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{a.name || a.id}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {a.id} • {a.currency || '—'} • {a.timezone_name || '—'}
                        </Typography>
                      </Box>
                      <Chip
                        label={`Status: ${a.account_status ?? '—'}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                          color: '#667eea',
                        }}
                      />
                    </Box>
                  </Paper>
                ))}
                {adAccounts.length > 10 && (
                  <Typography variant="caption" color="textSecondary">
                    Showing first 10 accounts.
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {connected && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                  Lead Ads (Instant Forms)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Pick the Facebook Page that runs your Lead Ads, then a form, and fetch submitted leads. You may need
                  to disconnect and connect again if you don’t see pages (new permissions: pages_show_list).
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small" disabled={pagesLoading}>
                    <InputLabel id="meta-page-label">Facebook Page</InputLabel>
                    <Select
                      labelId="meta-page-label"
                      label="Facebook Page"
                      value={selectedPageId}
                      onChange={(e) => setSelectedPageId(e.target.value)}
                    >
                      {pages.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name || p.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" disabled={formsLoading || !selectedPageId}>
                    <InputLabel id="meta-form-label">Lead form</InputLabel>
                    <Select
                      labelId="meta-form-label"
                      label="Lead form"
                      value={selectedFormId}
                      onChange={(e) => setSelectedFormId(e.target.value)}
                    >
                      {forms.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.name || f.id} {f.leads_count != null ? `(${f.leads_count} leads)` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    disabled={leadsLoading || !selectedPageId || !selectedFormId}
                    onClick={() => loadLeads()}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    {leadsLoading ? 'Loading…' : 'Fetch leads'}
                  </Button>
                </Stack>

                {pagesLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="textSecondary">
                      Loading pages…
                    </Typography>
                  </Box>
                ) : pages.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No pages returned. Ensure the connected user is an admin of the Page and reconnect with the latest
                    permissions.
                  </Typography>
                ) : null}

                {leads.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {leads.map((lead) => (
                      <Paper
                        key={lead.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: '14px', borderColor: 'rgba(102, 126, 234, 0.2)' }}
                      >
                        <Typography variant="caption" color="textSecondary" display="block">
                          {lead.created_time || '—'} · ID {lead.id}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {formatFieldData(lead.field_data)}
                        </Typography>
                      </Paper>
                    ))}
                    {leadsPaging?.cursors?.after && (
                      <Button
                        variant="outlined"
                        disabled={leadsLoading}
                        onClick={() => loadLeads(leadsPaging.cursors.after)}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                      >
                        Load more
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>
            </>
          )}

          <Typography variant="caption" color="textSecondary">
            Need help configuring Meta app settings? See Meta Developer settings for “Valid OAuth Redirect URIs”.
          </Typography>

          <Button
            component={RouterLink}
            to="/integrations/webhooks"
            variant="text"
            sx={{ textTransform: 'none', fontWeight: 700, width: 'fit-content' }}
          >
            Manage Webhooks
          </Button>
        </Stack>
      </Paper>
    </div>
  );
}

